import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../material/material.module';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ActivatedRoute, Router } from '@angular/router';
import { ProductPackage, ProductPackageControllerService } from '../../core/generated';
import { EncryptionUtil } from '../utils/encryption.util';
import { MatDialog } from '@angular/material/dialog';
import { ProductConfirmationDialogComponent } from '../dialogs/product-confirmation-dialog/product-confirmation-dialog.component';

@Component({
    selector: 'app-selec-new-profile',
    standalone: true,
    imports: [CommonModule, MaterialModule, FormsModule, ReactiveFormsModule],
    templateUrl: './selec-new-profile.component.html',
    styleUrl: './selec-new-profile.component.scss'
})
export class SelecNewProfileComponent implements OnInit {

  isMobile = inject(DeviceDetectorService).isMobile();
  productPackagesService = inject(ProductPackageControllerService);
  fb = inject(FormBuilder);
  router: Router = inject(Router);
  route = inject(ActivatedRoute);
  dialog = inject(MatDialog);
  productPackages: ProductPackage[] = [];
  private lastDialogProduct: ProductPackage | null = null;

  firstFormGroup = this.fb.group({
    userType: ['', Validators.required],
  });
  secondFormGroup = this.fb.group({
    plan: ['', Validators.required],
  });
  thirdFormGroup = this.fb.group({
    package: ['', Validators.required],
  });

  ngOnInit(): void {
    this.firstFormGroup.valueChanges.subscribe(() => {
      this.secondFormGroup.reset();
      this.thirdFormGroup.reset();
    });
    this.secondFormGroup.valueChanges.subscribe(() => {
      this.thirdFormGroup.reset();
    });

    this.thirdFormGroup.valueChanges.subscribe(() => {
      const product = this.findProduct();
      if (product && product !== this.lastDialogProduct) {
        this.openProductDialog(product);
      }
    });

    this.productPackagesService.findAll5(0, 300, 'name').subscribe((packages) => {
      this.productPackages = packages.content ?? [];
    });
  }

  openProductDialog(product: ProductPackage): void {
    this.lastDialogProduct = product;

    const dialogRef = this.dialog.open(ProductConfirmationDialogComponent, {
      width: '100%',
      maxWidth: '600px',
      data: { product }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.confirmSelection();
      } else {
        this.lastDialogProduct = null;
      }
    });
  }

  confirmSelection() {
    let finalSelection: ProductPackage.PackageTypeEnum;
    const userType = this.firstFormGroup.value.userType;
    const plan = this.secondFormGroup.value.plan;
    const pkg = this.thirdFormGroup.value.package;

    if (userType === 'PF') {
      finalSelection = plan as ProductPackage.PackageTypeEnum;
      const encryptedPackage = EncryptionUtil.encrypt(finalSelection);
      this.router.navigate(['/my-account/profiles/profileProviderPFForm'], {
        queryParams: { pkg: encryptedPackage }
      });
    } else if (userType === 'PJ') {
      if (plan === 'SUBSCRIPTION_ADMINISTRATOR') {
        finalSelection = plan as ProductPackage.PackageTypeEnum;
      } else {
        finalSelection = pkg as ProductPackage.PackageTypeEnum;
      }
      const encryptedPackage = EncryptionUtil.encrypt(finalSelection);
      this.router.navigate(['/my-account/profiles/profileProviderPJForm'], {
        queryParams: { pkg: encryptedPackage }
      });
    }
  }

  formatCurrencyValueByMonth(value: number | undefined): string {
    if (value === undefined) return '';
    const monthlyValue = value / 12;
    return monthlyValue.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }) + ' /mês';
  }

  formatCurrencyValue(value: number | undefined): string {
    if (value === undefined) return '';
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  findProduct(): ProductPackage | null {
    const userType = this.firstFormGroup.value.userType;
    const plan = this.secondFormGroup.value.plan;
    const pkg = this.thirdFormGroup.value.package;

    if (userType === 'PF') {
      return this.productPackages.find(ppk => ppk.packageType === plan) || null;
    } else if (userType === 'PJ') {
      if (plan === 'SUBSCRIPTION_ADMINISTRATOR') {
        return this.productPackages.find(ppk => ppk.packageType === 'SUBSCRIPTION_ADMINISTRATOR' as ProductPackage.PackageTypeEnum) || null;
      } else {
        return this.productPackages.find(ppk => ppk.packageType === pkg) || null;
      }
    }
    return null;
  }
}
